import { isNil, isEmpty, pathOr, has } from 'ramda';
import moment from 'moment';
import type Moment from 'moment';
import { getFloorPlanType } from '../utils';

const UTILITY_ALLOWANCE = 'UTILITY_ALLOWANCE';
const HUD_GROSS_RENT = 'HUD_GROSS_RENT';

const parseStartDateMs = (startDate: ?string): ?number => {
  if (!startDate) {
    return null;
  }

  const strictIsoDate = moment.utc(startDate, moment.ISO_8601, true);
  if (strictIsoDate.isValid()) {
    return strictIsoDate.valueOf();
  }

  const parsedStartDate = moment.utc(
    startDate,
    ['M/D/YYYY', 'MM/DD/YYYY', 'YYYY/M/D', 'YYYY/MM/DD'],
    true,
  );
  return parsedStartDate.isValid() ? parsedStartDate.valueOf() : null;
};

const pickRowWithLatestStartDate = (rows: Array<Object>): Object =>
  rows.reduce((best, row) => {
    const rowMs = parseStartDateMs(row?.startDate);
    const bestMs = parseStartDateMs(best?.startDate);

    if (rowMs != null && bestMs != null) {
      return rowMs >= bestMs ? row : best;
    }
    if (rowMs != null) {
      return row;
    }
    if (bestMs != null) {
      return best;
    }

    const rowStartDate = row?.startDate ?? '';
    const bestStartDate = best?.startDate ?? '';
    return rowStartDate >= bestStartDate ? row : best;
  });

/**
 * Schedule row with the latest startDate strictly before `beforeDate` — the
 * value in effect immediately prior to that date. Rows on or after
 * `beforeDate` (e.g. the record currently being edited) are excluded so a
 * record never resolves to itself. Returns null when nothing qualifies.
 */
const pickLatestRowBeforeDate = (
  rows: Array<Object>,
  beforeDate: ?string,
): Object | null => {
  const targetMs = parseStartDateMs(beforeDate);
  if (targetMs == null) {
    return null;
  }

  const priorRows = (rows || []).filter((row) => {
    const rowMs = parseStartDateMs(row?.startDate);
    return rowMs != null && rowMs < targetMs;
  });

  return priorRows.length > 0 ? pickRowWithLatestStartDate(priorRows) : null;
};

/** API lists HUD gross rent history as `fpHudGrossRents`; some callers use `hudGrossRents`. */
export const getHudGrossRentScheduleRows = (
  floorPlan: Object,
): Array<Object> => {
  const asHudGrossRents = pathOr([], ['hudGrossRents'], floorPlan);
  if (asHudGrossRents.length > 0) {
    return asHudGrossRents;
  }
  return pathOr([], ['fpHudGrossRents'], floorPlan);
};

/** Fee on a schedule row (fpua uses feeAmount; allowance headers may use utilityAllowanceAmount). */
export const getRowScheduleFee = (row: Object): number => {
  if (has('feeAmount', row)) {
    return Number(row?.feeAmount ?? NaN);
  }
  return Number(row?.utilityAllowanceAmount ?? NaN);
};

/**
 * Authoritative current HUD gross rent on the floor plan (matches portfolio
 * "current" limit). When `effectiveDate` (a date-only string, e.g.
 * 'YYYY-MM-DD') is passed, returns the HUD gross rent in effect immediately
 * prior to that date instead — i.e. the schedule row (from `hudGrossRents` /
 * `fpHudGrossRents`) with the latest startDate strictly before it. Returns
 * null when no such row exists (e.g. editing the first-ever history record).
 */
export const getHudGrossRentSnapshotFee = (
  floorPlan: Object,
  effectiveDate: ?string,
): ?number => {
  if (!effectiveDate) {
    return pathOr(null, ['HUDGrossRent'], floorPlan);
  }

  const priorRow = pickLatestRowBeforeDate(
    getHudGrossRentScheduleRows(floorPlan),
    effectiveDate,
  );
  return priorRow ? getRowScheduleFee(priorRow) : null;
};

/**
 * HUD Rent Schedule allowance header amount (matches floor plan UA panel when
 * schedules agree) — resolved from `fpua` when it has a HUD row, else
 * `allowances` (mirrors the rest of this file's fpua-vs-allowances
 * convention). Both are always filtered down to HUD rows first (`fpua` via
 * `ua.pha.isHUD`, `allowances` via `pha.isHUD`). When `effectiveDate` is
 * passed, returns the utility allowance in effect immediately prior to that
 * date instead. Returns null when no qualifying row exists.
 */
export const getHudUaSnapshotFee = (
  floorPlan: Object,
  effectiveDate: ?string,
): ?number => {
  const hudFpua = (floorPlan?.fpua ?? []).filter(
    (row) => row?.ua?.pha?.isHUD === true,
  );
  const hudAllowances = (floorPlan?.allowances ?? []).filter(
    (row) => row?.pha?.isHUD === true,
  );
  const rows = hudFpua.length > 0 ? hudFpua : hudAllowances;

  if (!effectiveDate) {
    const hudHeader = rows[0];
    return hudHeader ? getRowScheduleFee(hudHeader) : null;
  }

  const priorRow = pickLatestRowBeforeDate(rows, effectiveDate);
  return priorRow ? getRowScheduleFee(priorRow) : null;
};

/**
 * Active pricing/UA schedule row(s) have endDate == null. When multiple exist, prefer the row whose
 * fee matches snapshotFeeAmount (floor-plan truth), else the latest startDate.
 * (Do not use updatedAt — historical rows can be touched later than the current row.)
 */
export const getLatestActiveScheduleRow = (
  rows: Array<Object>,
  snapshotFeeAmount?: ?number,
): Object | null => {
  if (!rows || rows.length === 0) {
    return null;
  }
  const active = rows.filter((r) => r.endDate == null);
  if (active.length === 0) {
    return null;
  }
  if (active.length === 1) {
    return active[0];
  }

  const snap =
    snapshotFeeAmount != null && !Number.isNaN(Number(snapshotFeeAmount))
      ? Number(snapshotFeeAmount)
      : null;

  if (snap != null) {
    const matching = active.filter((r) => getRowScheduleFee(r) === snap);
    if (matching.length === 1) {
      return matching[0];
    }
    if (matching.length > 1) {
      return pickRowWithLatestStartDate(matching);
    }
  }

  return pickRowWithLatestStartDate(active);
};

export const dateSort = (a: Object, b: Object, order?: string = 'DESC') => {
  const oA = isNil(a) ? { updatedAt: new Date() } : a;
  const oB = isNil(b) ? { updatedAt: new Date() } : b;
  const dateATime = new Date(oA.updatedAt).getTime();
  const dateBTime = new Date(oB.updatedAt).getTime();
  if (order === 'DESC') {
    return dateBTime - dateATime;
  } else {
    return dateATime - dateBTime;
  }
};

export const isValidDateBuilder =
  (minStartDate: string) => (calendarDate: Moment) => {
    const defaultValue = moment.utc().startOf('date');
    const startDate = minStartDate
      ? moment.utc(minStartDate).add(1, 'days')
      : defaultValue;

    return calendarDate.isSameOrAfter(startDate);
  };

export const initializeFormValues = (floorPlansIds: Array<string>) => {
  let initialValues = {};
  (floorPlansIds || []).forEach((floorPlanId) => {
    initialValues[floorPlanId] = {
      newHudGrossRent: undefined,
      newHudUa: undefined,
      newHudUaStartDate: undefined,
      newHudGrossRentStartDate: undefined,
    };
  });
  return initialValues;
};

export const reduceFormValues = (values: Object, floorPlans: Array<Object>) => {
  let result = [];
  Object.keys(values).forEach((floorPlanId) => {
    const floorPlan = floorPlans.find((fp) => fp.id === floorPlanId) || {};
    if (
      Object.values(values[floorPlanId]).some((value) => value !== undefined)
    ) {
      if (
        !isNil(values[floorPlanId]['newHudGrossRent']) &&
        !isNil(values[floorPlanId]['newHudGrossRentStartDate'])
      ) {
        result.push({
          floorPlanId,
          pricingType: HUD_GROSS_RENT,
          feeAmount: parseFloat(values[floorPlanId]['newHudGrossRent']),
          startDate:
            values[floorPlanId]['newHudGrossRentStartDate'].format(
              'YYYY-MM-DD',
            ),
        });
      }

      if (
        !isNil(values[floorPlanId]['newHudUa']) &&
        !isNil(values[floorPlanId]['newHudUaStartDate'])
      ) {
        const isFpUa = pathOr([], ['fpua'], floorPlan).length > 0;
        const uaNamePath = ['pha', 'name'];
        const allowances = isFpUa
          ? pathOr([], ['fpua'], floorPlan)
          : pathOr([], ['allowances'], floorPlan);
        const latestUa = getLatestActiveScheduleRow(
          allowances,
          getHudUaSnapshotFee(floorPlan),
        );
        const name = pathOr(
          '',
          isFpUa ? ['ua'].concat(uaNamePath) : uaNamePath,
          latestUa,
        );

        result.push({
          floorPlanId,
          utilityAlowancesId: pathOr(
            '',
            isFpUa ? ['utilityAlowancesId'] : ['id'],
            latestUa,
          ),
          pricingType: `${name}_${UTILITY_ALLOWANCE}`,
          feeAmount: parseFloat(values[floorPlanId]['newHudUa']),
          startDate:
            values[floorPlanId]['newHudUaStartDate'].format('YYYY-MM-DD'),
        });
      }
    }
  });

  return result;
};

export const validatePairRequired = (
  values: Object,
  floorPlanId: string,
  isRent: boolean = false,
) => {
  const feeKey = isRent ? 'newHudGrossRent' : 'newHudUa';
  const feeValue = values[floorPlanId][feeKey];
  const dateKey = isRent ? 'newHudGrossRentStartDate' : 'newHudUaStartDate';
  const dateValue = values[floorPlanId][dateKey];
  const isFeeValueDefined = !isNil(feeValue) && !isEmpty(feeValue);
  const isDateValueDefined = !isNil(dateValue) && !isEmpty(dateValue);
  // prettier-ignore
  return {
    isValidPair: (isFeeValueDefined && isDateValueDefined) || (!isFeeValueDefined && !isDateValueDefined),
    requiredValue: !isFeeValueDefined ? feeKey : (!isDateValueDefined ? dateKey : ''),
  };
};

/**
 * Whether a floor plan belongs on the HUD gross rent screens. Both sources of truth count: the
 * legacy `hudFloorplan` column and a HUD affordable program association. They drift in either
 * direction, so reading only one of them drops floor plans that really are HUD.
 */
export const isHudFloorPlan = (floorPlan: ?Object): boolean => {
  if (!floorPlan) {
    return false;
  }

  return Boolean(floorPlan.hudFloorplan) || getFloorPlanType(floorPlan).isHUD;
};

export const processFloorPlanWithPricingResult = (
  floorPlans: Array<Object>,
): Array<Object> =>
  floorPlans.filter(isHudFloorPlan).map((floorPlan) => {
    const fpua = floorPlan.fpua ?? [];
    const allowances = floorPlan.allowances ?? [];

    return {
      ...floorPlan,
      fpua: fpua.filter((uaAllowance) => uaAllowance?.ua?.pha?.isHUD ?? false),
      allowances: allowances.filter(
        (allowance) => allowance?.pha?.isHUD ?? false,
      ),
    };
  });
